/**
 * Utilitaires pour l'affichage des heures de service ProPilote.
 *
 * Règle métier (validée Feb 17 2026) :
 *   - Les heures payables sont ARRONDIES au quart d'heure le plus proche
 *     (0.00 / 0.25 / 0.50 / 0.75).
 *   - L'affichage primaire reste en décimal (ex: 20.25h).
 *   - L'affichage humain "Xh YYmin" est utilisé en complément (PDF, tooltips).
 *
 * Exemples :
 *   9h 06min = 9.10h → roundToQuarterHour → 9.00h
 *   9h 08min = 9.13h → roundToQuarterHour → 9.25h
 *   9h 22min = 9.37h → roundToQuarterHour → 9.25h
 *   9h 23min = 9.38h → roundToQuarterHour → 9.50h
 */

/**
 * Arrondit une durée en heures décimales au quart d'heure le plus proche.
 * @param {number} decimalHours - Durée en heures (ex: 9.1 = 9h06min)
 * @returns {number} - Durée arrondie au 0.25 (ex: 9.0, 9.25, 9.5, 9.75)
 */
export const roundToQuarterHour = (decimalHours) => {
  if (typeof decimalHours !== 'number' || Number.isNaN(decimalHours)) return 0;
  return Math.round(decimalHours * 4) / 4;
};

/**
 * Formate une durée décimale en format humain "Xh YYmin".
 * Utilise les minutes EXACTES (pas l'arrondi) — pour usage en complément.
 * @param {number} decimalHours - Durée en heures
 * @returns {string} - Ex: "20h 09min" ou "0h" si zéro
 */
export const formatHoursHuman = (decimalHours) => {
  if (typeof decimalHours !== 'number' || Number.isNaN(decimalHours) || decimalHours <= 0) {
    return '0h';
  }
  const totalMinutes = Math.round(decimalHours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (m === 0) return `${h}h`;
  return `${h}h ${String(m).padStart(2, '0')}min`;
};

/**
 * Formate une durée décimale avec 2 décimales et "h" final.
 * @param {number} decimalHours - Durée en heures
 * @returns {string} - Ex: "20.25h"
 */
export const formatHoursDecimal = (decimalHours) => {
  if (typeof decimalHours !== 'number' || Number.isNaN(decimalHours)) return '--';
  return `${decimalHours.toFixed(2)}h`;
};

/**
 * Combine le format décimal arrondi + l'humain entre parenthèses.
 * Utilisé sur PDF et tooltips détaillés.
 * @param {number} decimalHours - Durée en heures (sera arrondie)
 * @returns {string} - Ex: "20.25h (20h 15min)"
 */
export const formatHoursDual = (decimalHours) => {
  if (typeof decimalHours !== 'number' || Number.isNaN(decimalHours) || decimalHours <= 0) {
    return '0h';
  }
  const rounded = roundToQuarterHour(decimalHours);
  return `${formatHoursDecimal(rounded)} (${formatHoursHuman(rounded)})`;
};
